import { Fragment, useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { MODULES } from "@/lib/nav";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const CODES = ["F", "FA:T", "F:T", "A:T", "V", "V:T", "L", "L:T", "S", "-"];
const LEGEND = { F: "Full", V: "View", A: "Approve", L: "Limited", S: "Self", "-": "None" };
const color = (c) => { const b = c.split(":")[0]; return b === "-" ? "text-muted-foreground/50" : b.includes("F") ? "text-[#3E6B48] bg-[#3E6B48]/10" : b.includes("A") ? "text-amber bg-amber/10" : b === "S" ? "text-indigo bg-indigo/10" : "text-primary bg-primary/10"; };

export default function RolesMatrix() {
  const { can, refresh } = useAuth();
  const [m, setM] = useState(null);
  const editable = can("admin.roles", "edit");
  const load = () => api.get("/permissions/matrix").then((r) => setM(r.data));
  useEffect(() => { load(); }, []);
  const cycle = async (key, role, cur) => {
    if (!editable || role === "superadmin") return;
    const next = CODES[(CODES.indexOf(cur) + 1) % CODES.length];
    try { await api.put("/permissions/override", { key, role, code: next }); await load(); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!m) return null;
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Administration" title="Roles & permissions" description={editable ? "Click any cell to cycle its access level for this organization. Super Admin is fixed." : "Access matrix for this organization."}
        actions={editable && <Button variant="outline" className="rounded-full" data-testid="permissions-reset-button" onClick={async () => { await api.delete("/permissions/override"); await load(); refresh(); toast.success("Reset to defaults"); }}><RotateCcw size={14} className="mr-1" /> Reset to defaults</Button>} />
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground rise">{Object.entries(LEGEND).map(([k, v]) => <span key={k}><b className="font-mono text-foreground">{k}</b> {v}</span>)}<span><b className="font-mono text-foreground">:T</b> team scope</span></div>
      <div className="paper-card overflow-x-auto rise" data-testid="permissions-matrix">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50"><tr><th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Module / Submodule</th>{m.roles.map((r) => <th key={r} className="px-2 py-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{m.labels[r]}</th>)}</tr></thead>
          <tbody>
            {MODULES.map((mod) => (
              <Fragment key={mod.id}>
                <tr className="bg-secondary/20"><td colSpan={7} className="px-4 py-2 font-serif font-semibold">{mod.label}</td></tr>
                {mod.items.map(([key, label]) => (
                  <tr key={key} className="border-t border-dashed border-border">
                    <td className="px-4 py-1.5 text-muted-foreground">{label}</td>
                    {m.roles.map((r) => { const c = m.matrix[key]?.[r] || "-"; const overridden = m.defaults[key]?.[r] !== c; return (
                      <td key={r} className="px-2 py-1.5 text-center">
                        <button data-testid={`permission-toggle-${r}-${key.replace(".", "-")}`} disabled={!editable || r === "superadmin"} onClick={() => cycle(key, r, c)} className={cn("font-mono text-[11px] px-2 py-0.5 rounded min-w-[44px] transition-colors", color(c), editable && r !== "superadmin" && "hover:ring-1 ring-primary cursor-pointer", overridden && "ring-1 ring-amber")}>{c}</button>
                      </td>); })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
